import { createContext, useContext, useMemo, useEffect, PropsWithChildren } from 'react';
import { useThemeQuery } from 'app/data/queries/use-theme-query';
import { Theme } from 'app/data/types/theme';
import { useSdkData } from 'app/shared/hooks/use-sdk-data/use-sdk-data.hook';
import { ErrorNotification } from 'app/shared/types/errors.types';

interface ThemeContextValue {
  theme: Theme | null;
  isLoading: boolean;
  error: ErrorNotification | null;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const { sdkTheme } = useSdkData();
  const { theme, isLoading, error } = useThemeQuery(sdkTheme);

  // Set CSS variables with custom theme
  useEffect(() => {
    if (!theme) return;

    const root = document.documentElement;

    if (theme.primary1) {
      root.style.setProperty('--primary', theme.primary1);
      root.style.setProperty('--ring', theme.primary1);
      root.style.setProperty('--accent', theme.primary1);
    }

    if (theme.primary_color) {
      root.style.setProperty('--primary', theme.primary_color);
      root.style.setProperty('--ring', theme.primary_color);
      root.style.setProperty('--accent', theme.primary_color);
    }

    if (theme.secondary_color) {
      root.style.setProperty('--secondary', theme.secondary_color);
      root.style.setProperty('--muted', theme.secondary_color);
    }

    if (theme.primary_font_color) {
      root.style.setProperty('--primary-font-color', theme.primary_font_color);
    }

    if (theme.secondary_font_color) {
      root.style.setProperty('--secondary-font-color', theme.secondary_font_color);
    }

    if (theme.gradient_1) {
      root.style.setProperty('--gradient-1', theme.gradient_1);
    }

    if (theme.gradient_2) {
      root.style.setProperty('--gradient-2', theme.gradient_2);
    }

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      root.style.removeProperty('--primary');
      root.style.removeProperty('--secondary');
      root.style.removeProperty('--accent');
      root.style.removeProperty('--ring');
      root.style.removeProperty('--muted');
      root.style.removeProperty('--primary-font-color');
      root.style.removeProperty('--secondary-font-color');
      root.style.removeProperty('--gradient-1');
      root.style.removeProperty('--gradient-2');
    };
  }, [theme]);

  const value = useMemo(() => ({
    theme: theme || null,
    isLoading,
    error,
  }), [theme, isLoading, error]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
